// CuratedHomepage Component - Kuratierte Sektionen statt einfachem Grid
// GoodLife: Emotionale Kategorien (Beliebt, Ruhig, Gemuetlich, Kreativ, Natur, etc.)
// GoodWork: Arbeits-Kategorien (Fokus, Austausch, Inspiration, Werkstatt, Wissen)

import React, { useMemo } from 'react';
import type { Place, MainCategory } from '../../types';
import { PlaceSection } from './PlaceSection';
import { PlaceGrid } from '../places/PlaceGrid';
import { PersonalizedRecommendation } from '../discovery/PersonalizedRecommendation';
import { useRealm } from '../../context/RealmContext';
import { Target, Users, Lightbulb, Wrench, GraduationCap } from 'lucide-react';

interface CuratedHomepageProps {
  places: Place[];
  favorites: string[];
  onSelectPlace: (place: Place) => void;
  onToggleFavorite: (placeId: string) => void;
  selectedMood?: string | null;
  activeCategory?: MainCategory | '';
  showAllPlaces?: boolean;
}

export function CuratedHomepage({
  places,
  favorites,
  onSelectPlace,
  onToggleFavorite,
  selectedMood,
  activeCategory,
  showAllPlaces = false,
}: CuratedHomepageProps) {
  const { isGoodWork } = useRealm();

  // GoodLife curated sections
  const goodLifeSections = useMemo(() => {
    if (isGoodWork || showAllPlaces || activeCategory) return null;

    // Popular places (highest rated with most reviews)
    const popularPlaces = [...places]
      .filter((p) => p.rating >= 4 || p.reviewCount >= 3)
      .sort((a, b) => {
        const scoreA = a.rating * 2 + Math.min(a.reviewCount, 10) * 0.5;
        const scoreB = b.rating * 2 + Math.min(b.reviewCount, 10) * 0.5;
        return scoreB - scoreA;
      })
      .slice(0, 8);

    // Quiet & relaxing places
    const quietPlaces = places
      .filter((p) =>
        p.category === 'silent_sanctuary' ||
        p.moods?.includes('Ruhig & entspannt') ||
        p.vibeTags?.includes('empfangsbereit')
      )
      .slice(0, 8);

    // Cozy places
    const cozyPlaces = places
      .filter((p) =>
        p.category === 'public_living_room' ||
        p.moods?.includes('Gemütlich & vertraut') ||
        p.vibeTags?.includes('anlasslos')
      )
      .slice(0, 8);

    // Creative & inspiring places
    const creativePlaces = places
      .filter((p) =>
        p.category === 'urban_creative' ||
        p.moods?.includes('Kreativ & inspirierend') ||
        p.vibeTags?.includes('selbstueberraschung')
      )
      .slice(0, 8);

    // Nature escapes
    const naturePlaces = places
      .filter((p) => p.category === 'natur_outdoor')
      .slice(0, 8);

    // Free / no consumption pressure
    const freePlaces = places
      .filter((p) =>
        p.vibeTags?.includes('offen_fuer_alle') ||
        p.category === 'natur_outdoor' ||
        p.category === 'silent_sanctuary'
      )
      .slice(0, 8);

    // Newest places
    const newestPlaces = [...places]
      .sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 8);

    return { popular: popularPlaces, quiet: quietPlaces, cozy: cozyPlaces, creative: creativePlaces, nature: naturePlaces, free: freePlaces, newest: newestPlaces };
  }, [places, showAllPlaces, activeCategory, isGoodWork]);

  // GoodWork curated sections
  const goodWorkSections = useMemo(() => {
    if (!isGoodWork || showAllPlaces || activeCategory) return null;

    // Fokus - focused_work + #fokussiert
    const fokusPlaces = places
      .filter((p) =>
        p.goodworkCategory === 'focused_work' ||
        p.goodworkVibeTags?.includes('fokussiert')
      )
      .slice(0, 8);

    // Austausch - coworking, meeting_space + #kollaborativ
    const austauschPlaces = places
      .filter((p) =>
        p.goodworkCategory === 'coworking' ||
        p.goodworkCategory === 'meeting_space' ||
        p.goodworkVibeTags?.includes('kollaborativ') ||
        p.goodworkVibeTags?.includes('community')
      )
      .slice(0, 8);

    // Inspiration - creative_studio + #inspirierend
    const inspirationPlaces = places
      .filter((p) =>
        p.goodworkCategory === 'creative_studio' ||
        p.goodworkVibeTags?.includes('inspirierend')
      )
      .slice(0, 8);

    // Werkstatt - maker_space
    const werkstattPlaces = places
      .filter((p) => p.goodworkCategory === 'maker_space')
      .slice(0, 8);

    // Wissen - learning_hub
    const wissenPlaces = places
      .filter((p) => p.goodworkCategory === 'learning_hub')
      .slice(0, 8);

    // Newest GoodWork places
    const newestPlaces = [...places]
      .sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt instanceof Date ? b.createdAt : b.createdAt?.toDate?.() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 8);

    return { fokus: fokusPlaces, austausch: austauschPlaces, inspiration: inspirationPlaces, werkstatt: werkstattPlaces, wissen: wissenPlaces, newest: newestPlaces };
  }, [places, showAllPlaces, activeCategory, isGoodWork]);

  // Combined sections reference for backward compatibility
  const sections = isGoodWork ? goodWorkSections : goodLifeSections;

  // If mood is selected, filter for mood-relevant places
  const moodFilteredPlaces = useMemo(() => {
    if (!selectedMood) return null;

    // GoodLife moods
    switch (selectedMood) {
      case 'calm':
        return places.filter((p) =>
          p.category === 'silent_sanctuary' ||
          p.moods?.includes('Ruhig & entspannt') ||
          p.vibeTags?.includes('empfangsbereit')
        );
      case 'cozy':
        return places.filter((p) =>
          p.category === 'public_living_room' ||
          p.moods?.includes('Gemütlich & vertraut') ||
          p.vibeTags?.includes('anlasslos')
        );
      case 'creative':
        return places.filter((p) =>
          p.category === 'urban_creative' ||
          p.moods?.includes('Kreativ & inspirierend')
        );
      case 'lively':
      case 'social':
        return places.filter((p) =>
          p.moods?.includes('Lebendig & gesellig') ||
          p.vibeTags?.includes('schnack_potential') ||
          p.category === 'public_living_room'
        );
      case 'surprise':
        // Random selection
        return [...places].sort(() => Math.random() - 0.5).slice(0, 12);
      // GoodWork moods
      case 'fokus':
        return places.filter((p) =>
          p.goodworkCategory === 'focused_work' ||
          p.goodworkVibeTags?.includes('fokussiert')
        );
      case 'austausch':
        return places.filter((p) =>
          p.goodworkCategory === 'coworking' ||
          p.goodworkCategory === 'meeting_space' ||
          p.goodworkVibeTags?.includes('kollaborativ') ||
          p.goodworkVibeTags?.includes('community')
        );
      case 'inspiration':
        return places.filter((p) =>
          p.goodworkCategory === 'creative_studio' ||
          p.goodworkVibeTags?.includes('inspirierend')
        );
      case 'werkstatt':
        return places.filter((p) =>
          p.goodworkCategory === 'maker_space'
        );
      case 'wissen':
        return places.filter((p) =>
          p.goodworkCategory === 'learning_hub'
        );
      default:
        return null;
    }
  }, [places, selectedMood]);

  // If filters are active or showing all, show grid
  if (showAllPlaces || activeCategory || moodFilteredPlaces) {
    const displayPlaces = moodFilteredPlaces || places;
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {displayPlaces.length} {displayPlaces.length === 1 ? 'Ort' : 'Orte'} gefunden
          </h2>
        </div>
        <PlaceGrid
          places={displayPlaces}
          favorites={favorites}
          loading={false}
          onSelect={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      </div>
    );
  }

  // Show curated sections
  if (!sections) return null;

  // GoodWork sections with Lucide icons
  if (isGoodWork && goodWorkSections) {
    return (
      <div className="space-y-4">
        {/* Personalized Recommendation based on GoodWork Crafting */}
        <PersonalizedRecommendation
          places={places}
          onPlaceClick={onSelectPlace}
        />

        {/* Fokus */}
        {goodWorkSections.fokus.length > 0 && (
          <PlaceSection
            icon={<Target className="w-5 h-5 text-indigo-600" />}
            title="Fokus"
            subtitle="Orte fuer konzentriertes Arbeiten"
            places={goodWorkSections.fokus}
            favorites={favorites}
            onSelectPlace={onSelectPlace}
            onToggleFavorite={onToggleFavorite}
          />
        )}

        {/* Austausch */}
        {goodWorkSections.austausch.length > 0 && (
          <PlaceSection
            icon={<Users className="w-5 h-5 text-indigo-600" />}
            title="Austausch"
            subtitle="Orte fuer Zusammenarbeit & Netzwerk"
            places={goodWorkSections.austausch}
            favorites={favorites}
            onSelectPlace={onSelectPlace}
            onToggleFavorite={onToggleFavorite}
          />
        )}

        {/* Inspiration */}
        {goodWorkSections.inspiration.length > 0 && (
          <PlaceSection
            icon={<Lightbulb className="w-5 h-5 text-indigo-600" />}
            title="Inspiration"
            subtitle="Orte fuer kreative Koepfe"
            places={goodWorkSections.inspiration}
            favorites={favorites}
            onSelectPlace={onSelectPlace}
            onToggleFavorite={onToggleFavorite}
          />
        )}

        {/* Werkstatt */}
        {goodWorkSections.werkstatt.length > 0 && (
          <PlaceSection
            icon={<Wrench className="w-5 h-5 text-indigo-600" />}
            title="Werkstatt"
            subtitle="Orte zum Erschaffen & Bauen"
            places={goodWorkSections.werkstatt}
            favorites={favorites}
            onSelectPlace={onSelectPlace}
            onToggleFavorite={onToggleFavorite}
          />
        )}

        {/* Wissen */}
        {goodWorkSections.wissen.length > 0 && (
          <PlaceSection
            icon={<GraduationCap className="w-5 h-5 text-indigo-600" />}
            title="Wissen"
            subtitle="Orte zum Lernen & Wachsen"
            places={goodWorkSections.wissen}
            favorites={favorites}
            onSelectPlace={onSelectPlace}
            onToggleFavorite={onToggleFavorite}
          />
        )}

        {/* Newest GoodWork Places */}
        {goodWorkSections.newest.length > 0 && (
          <PlaceSection
            emoji="✨"
            title="Neu entdeckt"
            subtitle="Frisch hinzugefuegte GoodWork Places"
            places={goodWorkSections.newest}
            favorites={favorites}
            onSelectPlace={onSelectPlace}
            onToggleFavorite={onToggleFavorite}
          />
        )}
      </div>
    );
  }

  // GoodLife sections with emojis (default)
  return (
    <div className="space-y-4">
      {/* Personalized Recommendation based on GoodWork Crafting */}
      <PersonalizedRecommendation
        places={places}
        onPlaceClick={onSelectPlace}
      />

      {/* Popular Places */}
      {goodLifeSections?.popular && goodLifeSections.popular.length > 0 && (
        <PlaceSection
          emoji="🔥"
          title="Gerade beliebt"
          subtitle="Die Lieblingsorte unserer Community"
          places={goodLifeSections.popular}
          favorites={favorites}
          onSelectPlace={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {/* Quiet Retreats */}
      {goodLifeSections?.quiet && goodLifeSections.quiet.length > 0 && (
        <PlaceSection
          emoji="🌿"
          title="Für einen ruhigen Moment"
          subtitle="Orte der Stille und Entspannung"
          places={goodLifeSections.quiet}
          favorites={favorites}
          onSelectPlace={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {/* Cozy Spots */}
      {goodLifeSections?.cozy && goodLifeSections.cozy.length > 0 && (
        <PlaceSection
          emoji="☕"
          title="Gemütliche Wohnzimmer"
          subtitle="Hier darfst du einfach sein"
          places={goodLifeSections.cozy}
          favorites={favorites}
          onSelectPlace={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {/* Creative & Inspiring */}
      {goodLifeSections?.creative && goodLifeSections.creative.length > 0 && (
        <PlaceSection
          emoji="🎨"
          title="Kreativ & Inspirierend"
          subtitle="Orte voller Energie und Kunst"
          places={goodLifeSections.creative}
          favorites={favorites}
          onSelectPlace={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {/* Nature Escapes */}
      {goodLifeSections?.nature && goodLifeSections.nature.length > 0 && (
        <PlaceSection
          emoji="🌳"
          title="Raus ins Grüne"
          subtitle="Natur und frische Luft"
          places={goodLifeSections.nature}
          favorites={favorites}
          onSelectPlace={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {/* Free / No Consumption */}
      {goodLifeSections?.free && goodLifeSections.free.length > 0 && (
        <PlaceSection
          emoji="🎁"
          title="Kostenlos genießen"
          subtitle="Orte ohne Konsumzwang"
          places={goodLifeSections.free}
          favorites={favorites}
          onSelectPlace={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      )}

      {/* Newest Discoveries */}
      {goodLifeSections?.newest && goodLifeSections.newest.length > 0 && (
        <PlaceSection
          emoji="✨"
          title="Neu entdeckt"
          subtitle="Frisch hinzugefügt"
          places={goodLifeSections.newest}
          favorites={favorites}
          onSelectPlace={onSelectPlace}
          onToggleFavorite={onToggleFavorite}
        />
      )}
    </div>
  );
}

export default CuratedHomepage;
